import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, Calendar, X, ZoomIn } from "lucide-react";
import HeaderBar from "@/auth/HeaderBar";
import Footer from "@/components/landing/Footer";
import { useCart } from "@/cart/CartProvider";
import { getPostBySlug, getSortedPosts } from "@/data/blogPosts";

export default function BlogPost() {
    const { slug } = useParams();
    const post = getPostBySlug(slug);
    const { openAuth } = useCart();
    const [lightbox, setLightbox] = useState(null); // { src, alt }

    useEffect(() => {
        window.scrollTo(0, 0);
        if (post) document.title = `${post.title} · Caribbean Meal Prep`;
    }, [post]);

    useEffect(() => {
        if (!lightbox) return;
        const onKey = (e) => e.key === "Escape" && setLightbox(null);
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [lightbox]);

    if (!post) return <Navigate to="/blogs" replace />;

    const otherPosts = getSortedPosts()
        .filter((p) => p.slug !== post.slug)
        .slice(0, 3);

    return (
        <div className="min-h-screen bg-cream text-navy font-sans flex flex-col">
            <div className="relative hero-gradient text-white pb-16 pt-32 px-6">
                <HeaderBar onSignIn={openAuth} dark />
                <div className="max-w-3xl mx-auto">
                    <Link
                        to="/blogs"
                        data-testid="blogpost-back"
                        className="inline-flex items-center gap-1.5 text-mint hover:text-gold transition-colors text-sm font-medium"
                    >
                        <ArrowLeft size={16} /> All posts
                    </Link>
                    {post.category && (
                        <p className="mt-6 text-[11px] tracking-[0.4em] text-gold font-bold uppercase">
                            {post.category}
                        </p>
                    )}
                    <h1
                        data-testid="blogpost-title"
                        className="mt-3 font-serif font-black text-3xl sm:text-4xl md:text-5xl leading-[1.1]"
                    >
                        {post.title}
                    </h1>
                    <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-white/80">
                        <span className="inline-flex items-center gap-1.5">
                            <Calendar size={14} />
                            {post.date}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <Clock size={14} />
                            {post.read_time}
                        </span>
                        <span className="opacity-70">· by {post.author}</span>
                    </div>
                </div>
            </div>

            <main className="flex-1 max-w-3xl w-full mx-auto px-6 -mt-10">
                {/* Clickable hero image */}
                <button
                    type="button"
                    data-testid="blogpost-hero-trigger"
                    onClick={() =>
                        setLightbox({ src: post.hero_image, alt: post.title })
                    }
                    className="relative w-full block rounded-[20px] overflow-hidden shadow-[0_18px_50px_rgba(13,40,24,0.18)] group bg-mint/30 cursor-zoom-in"
                    aria-label="Open hero image"
                >
                    <img
                        src={post.hero_image}
                        alt={post.title}
                        className="w-full h-auto block"
                    />
                    <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 backdrop-blur text-white text-[10px] font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                        <ZoomIn size={11} /> Tap to enlarge
                    </span>
                </button>

                <article className="prose prose-legal mt-10 pb-16">
                    {post.content.map((block, i) => (
                        <Block
                            key={i}
                            block={block}
                            onImageClick={(src, alt) => setLightbox({ src, alt })}
                        />
                    ))}
                </article>

                {/* CTA */}
                <div
                    data-testid="blogpost-cta"
                    className="mb-16 rounded-2xl bg-forest text-white p-7 sm:p-8 text-center"
                >
                    <p className="text-gold text-[11px] tracking-[0.4em] font-bold uppercase">
                        Ready to start?
                    </p>
                    <h3 className="mt-2 font-serif font-black text-2xl sm:text-3xl">
                        Build your weekly box in 3 minutes
                    </h3>
                    <p className="mt-3 text-white/85 text-sm sm:text-base max-w-md mx-auto">
                        Mix &amp; match Caribbean meals — discounts auto-apply
                        at 3, 6, 9, and 12 plates.
                    </p>
                    <Link
                        to="/menu"
                        className="mt-5 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gold hover:bg-goldDark text-navy font-bold transition-all active:scale-95 shadow-[0_10px_28px_rgba(244,164,53,0.45)]"
                    >
                        Order this week's meals →
                    </Link>
                </div>

                {/* Other posts */}
                {otherPosts.length > 0 && (
                    <section className="mb-20">
                        <h3 className="font-serif font-black text-2xl text-navy mb-5">
                            More from the blog
                        </h3>
                        <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {otherPosts.map((p) => (
                                <li key={p.slug}>
                                    <Link
                                        to={`/blogs/${p.slug}`}
                                        className="block bg-white rounded-2xl border border-mint overflow-hidden hover:-translate-y-0.5 transition-transform"
                                    >
                                        <div className="aspect-[4/3] overflow-hidden bg-mint/30">
                                            <img
                                                src={p.hero_image}
                                                alt={p.title}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                        </div>
                                        <p className="p-4 font-serif font-bold text-navy text-base leading-snug line-clamp-3">
                                            {p.title}
                                        </p>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
            </main>

            <Footer />

            {lightbox && (
                <div
                    data-testid="blog-lightbox"
                    role="dialog"
                    aria-modal="true"
                    onClick={() => setLightbox(null)}
                    className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-up"
                >
                    <button
                        data-testid="blog-lightbox-close"
                        onClick={() => setLightbox(null)}
                        aria-label="Close"
                        className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
                    >
                        <X size={20} />
                    </button>
                    <img
                        src={lightbox.src}
                        alt={lightbox.alt}
                        onClick={(e) => e.stopPropagation()}
                        className="max-h-[90vh] max-w-[94vw] rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.6)] bg-cream"
                        draggable={false}
                    />
                </div>
            )}
        </div>
    );
}

function Block({ block, onImageClick }) {
    switch (block.type) {
        case "h2":
            return <h2>{block.text}</h2>;
        case "p":
            return (
                <p dangerouslySetInnerHTML={{ __html: block.text }} />
            );
        case "ul":
            return (
                <ul>
                    {block.items.map((it, i) => (
                        <li key={i} dangerouslySetInnerHTML={{ __html: it }} />
                    ))}
                </ul>
            );
        case "image":
            return (
                <figure className="not-prose my-8">
                    <button
                        type="button"
                        onClick={() => onImageClick(block.src, block.alt)}
                        className="relative block w-full rounded-2xl overflow-hidden border border-mint shadow-[0_12px_30px_rgba(13,40,24,0.15)] cursor-zoom-in group"
                        aria-label="Open image"
                    >
                        <img
                            src={block.src}
                            alt={block.alt || ""}
                            className="w-full h-auto block"
                            loading="lazy"
                        />
                        <span className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 backdrop-blur text-white text-[10px] font-bold uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-opacity">
                            <ZoomIn size={11} /> Enlarge
                        </span>
                    </button>
                    {block.caption && (
                        <figcaption className="mt-3 text-sm text-muted2 italic text-center">
                            {block.caption}
                        </figcaption>
                    )}
                </figure>
            );
        case "youtube": {
            const isShort = block.short !== false; // default to short (vertical 9:16)
            const aspect = isShort ? "aspect-[9/16]" : "aspect-video";
            const wrap = isShort ? "max-w-sm mx-auto" : "w-full";
            return (
                <figure className={`not-prose my-8 ${wrap}`}>
                    <div
                        className={`${aspect} rounded-2xl overflow-hidden bg-black shadow-[0_12px_30px_rgba(13,40,24,0.18)]`}
                    >
                        <iframe
                            src={`https://www.youtube.com/embed/${block.videoId}?rel=0&modestbranding=1`}
                            title={block.title || "Caribbean Meal Prep video"}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            className="w-full h-full"
                            loading="lazy"
                        />
                    </div>
                    {block.caption && (
                        <figcaption className="mt-3 text-sm text-muted2 italic text-center">
                            {block.caption}
                        </figcaption>
                    )}
                </figure>
            );
        }
        default:
            return null;
    }
}
